'use client';

import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../lib';

/**
 * Provides context for tooltip components, managing delay durations.
 */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

interface ITooltipProps extends ComponentProps<typeof TooltipPrimitive.Root> {
  delayDuration?: number;
}

/**
 * Root container for a tooltip component, based on Radix UI's Tooltip primitive.
 */
function Tooltip({ ...props }: ITooltipProps) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

/**
 * The element that triggers the tooltip to appear.
 */
function TooltipTrigger({
  ...props
}: ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * The content that appears within the tooltip.
 */
function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={cn(
          `bg-card-primary-bg text-foreground animate-in fade-in-0 zoom-in-95
          data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin)
          text-balance z-50 w-fit rounded-md border border-gray-300 px-3 py-1.5 text-xs
          dark:border-gray-700/50`,
          className
        )}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className="
            bg-card-primary-bg fill-card-primary-bg size-2.5 z-50
            translate-y-[calc(-50%_-_1px)] rotate-45 rounded-[2px] border
            border-gray-300  !border-l-transparent !border-t-transparent
            dark:border-gray-700/50
          "
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface ISimpleTooltipProps extends ITooltipProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
  content: ReactNode;
}

/**
 * A simplified tooltip component that wraps around children and displays a message.
 */
export function SimpleTooltip({
  asChild = true,
  children,
  content,
  className,
  ...rest
}: PropsWithChildren<ISimpleTooltipProps>) {
  return (
    <Tooltip {...rest}>
      <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
      <TooltipContent className={className}>{content}</TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
